import React, { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import FunnyLoadingMessage from '@components/FunnyLoadingMessage';
import { NewPostsPill } from '@components/NewPostsPill';
import { QuestionCardSkeleton } from '@components/QuestionCardSkeleton';
import { PullRefreshIndicator } from '@components/ui/pull-refresh-indicator';
import { featureFlags } from '@config/featureFlags';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { useFeedEngagement } from '@hooks/useFeedEngagement';
import { useMyJoinedSpaces } from '@hooks/useMyJoinedSpaces';
import { usePullToRefresh } from '@hooks/usePullToRefresh';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import { useAuth } from '@providers';
import { useAppStore } from '@stores/appStore';

// Local components and hooks
import { HomeFeed } from './components/HomeFeed';
import { HomeEmptyState } from './components/HomeEmptyState';
import { HomeFAB } from './components/HomeFAB';
import { HomeTabBar } from './components/HomeTabBar';
import { SpacesCarousel } from './components/SpacesCarousel';
import { LiveHomePill } from './components/LiveHomePill';
import { LiveHomeRecap } from './components/LiveHomeRecap';
import { LiveMeetversePills } from './components/LiveMeetversePills';
import { MySpacesEmptyState } from './components/MySpacesEmptyState';
import { useHomeFeed } from './hooks/useHomeFeed';
import { useMySpacesFeed } from './hooks/useMySpacesFeed';
import { useFeedSorting } from './hooks/useFeedSorting';
import { useScrollTracking } from './hooks/useScrollTracking';

const QUESTION_FEED_SKELETON_KEYS = Array.from(
  { length: 6 },
  (_, index) => `questions-skeleton-${index}`,
);

/**
 * Home screen component displaying the main question feed
 *
 * Two-tab layout:
 * - Meetverse: Global feed visible to everyone (default for guests)
 * - My Spaces: Aggregated feed from user's joined spaces (auth required)
 *
 * Each tab has independent sort state persisted in appStore.
 * Tab selection persists to localStorage via zustand persist middleware.
 *
 * Note: Scroll-to-top after question creation is handled by ScrollRestoration
 * in RootLayout.tsx via location.state.scrollToTop - no manual handling needed here
 */
export const Home: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, showAuthenticatedUI } = useAuth();
  const { activeTab, hasExplicitlyChosenTab, sortByTab, setActiveTab, setSortByForTab } =
    useAppStore();
  const { trackEvent } = useAmplitudeTracking();
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  // Authenticated users who haven't explicitly chosen a tab default to My Spaces.
  // Guests always see Meetverse (the store default).
  // Computed during render — no useEffect needed.
  const effectiveTab = showAuthenticatedUI && !hasExplicitlyChosenTab ? 'my-spaces' : activeTab;

  // My Spaces keeps per-tab sort. Meetverse sorting was removed — sortBy is `undefined`
  // there so no downstream consumer (filter, analytics, UI) runs sort-related logic.
  const mySpacesSortBy = sortByTab['my-spaces'];
  const sortBy = effectiveTab === 'my-spaces' ? mySpacesSortBy : undefined;

  // --- Data fetching (both hooks always called — React rules of hooks) ---
  const isMySpacesActive = effectiveTab === 'my-spaces' && isAuthenticated;

  // Live surfaces (pill + recap) are my-spaces-only (spec §5.1: "Meetverse tab: nothing"). Gating
  // here — not inside the leaf components — keeps their `useNow` timers from mounting on Meetverse
  // or when the kill switch is off. Home itself never calls a live hook, so it does NOT tick; the
  // leaf slots own their ticks (spec §3), leaving the feed list below untouched every 30s.
  const showLiveSurfaces = featureFlags.liveEvents && isMySpacesActive;

  // Meetverse feed — disabled when on My Spaces tab to avoid wasteful polling
  const meetverseFeed = useHomeFeed(effectiveTab === 'meetverse');

  // My Spaces feed — only enabled on my-spaces tab AND when authenticated
  const mySpacesFeed = useMySpacesFeed({
    sortBy: mySpacesSortBy,
    enabled: isMySpacesActive,
  });

  // Joined spaces data — for carousel visibility and empty state variant
  const { data: joinedSpaces, isLoading: spacesLoading } = useMyJoinedSpaces({
    enabled: isMySpacesActive,
  });
  const hasSpaces = joinedSpaces.length > 0;

  // Show skeleton while auth initializes for returning users — disabled queries
  // return isLoading:false in TanStack Query v5, so we guard against premature empty-state display
  const showMySpacesLoading =
    effectiveTab === 'my-spaces' && (spacesLoading || (authLoading && showAuthenticatedUI));

  // Derive the active feed based on tab
  const activeFeed = effectiveTab === 'my-spaces' ? mySpacesFeed : meetverseFeed;

  const { questions, isLoading, isLoadingMore, hasNextPage, fetchNextPage, isEmpty, isError } =
    activeFeed;

  // Pull-to-refresh
  const { pullDistance, isRefreshing, maxPullDistance, bindContainer } = usePullToRefresh({
    onRefresh: () => activeFeed.refresh(),
    disabled:
      isLoading || showMySpacesLoading || (effectiveTab === 'my-spaces' && !isAuthenticated),
  });

  // Sorting — bookmarked filter is client-side
  const sortedQuestions = useFeedSorting({
    questions,
    sortBy,
    isAuthenticated,
  });

  // Analytics — scroll tracking
  useScrollTracking({
    questionCount: sortedQuestions.length,
    sortBy,
    isAuthenticated,
    enabled: !isLoading && sortedQuestions.length > 0,
  });

  // Feed engagement tracking
  useFeedEngagement({
    eventFilterIds: [],
    tab: effectiveTab,
    sortOrder: sortBy,
    isAuthenticated,
  });

  // Track initial feed view — fires once when questions first load
  const hasTrackedInitialView = useRef(false);
  useEffect(() => {
    if (!hasTrackedInitialView.current && !isLoading && questions.length > 0) {
      trackEvent(ANALYTICS_EVENTS.feed_viewed, {
        tab: effectiveTab,
        question_count: questions.length,
        is_authenticated: isAuthenticated,
        ...(sortBy ? { sort_by: sortBy } : {}),
      });
      hasTrackedInitialView.current = true;
    }
  }, [isAuthenticated, questions.length, isLoading, effectiveTab, sortBy, trackEvent]);

  // Stable callback for navigation
  const handleCreateQuestion = useCallback(() => {
    navigate('/create-question');
  }, [navigate]);

  return (
    <>
      <title>Home | Meetball</title>
      <meta
        name="description"
        content="Connect with your community. Post what you need, share knowledge, and engage with event participants."
      />
      <meta property="og:title" content="Home | Meetball" />
      <meta
        property="og:description"
        content="Connect with your community. Post what you need, share knowledge, and engage with event participants."
      />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={currentUrl} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Home | Meetball" />
      <meta
        name="twitter:description"
        content="Connect with your community. Post what you need, share knowledge, and engage with event participants."
      />

      <div {...bindContainer} className="pb-nav-safe">
        {/* Sticky Tab Bar */}
        <div
          className="sticky z-20 bg-mb-bg -mt-px"
          style={{ top: 'calc(var(--header-height) - 1px)' }}
        >
          <div className="w-full max-w-2xl mx-auto px-4 pt-1 pb-1">
            <HomeTabBar
              activeTab={effectiveTab}
              onTabChange={tab => {
                setActiveTab(tab);
                trackEvent(ANALYTICS_EVENTS.home_tab_changed, {
                  from_tab: effectiveTab,
                  to_tab: tab,
                });
              }}
              sortBy={sortBy}
              onSortChange={sort => {
                setSortByForTab(effectiveTab, sort);
                trackEvent(ANALYTICS_EVENTS.feed_sort_changed, {
                  sort_by: sort,
                  tab: effectiveTab,
                });
              }}
            />
          </div>
          {/* Gradient fade below tabs — overlaps content so it fades in under the tabs */}
          <div className="fade-below-sticky" />
        </div>

        {/* "New posts" pill — meetverse only. Detector poll is its own
            useQuery; the pill mounts/unmounts based on hasNewContent. */}
        {effectiveTab === 'meetverse' && <NewPostsPill isBusy={isRefreshing} />}

        {/* Pull-to-Refresh Indicator */}
        <PullRefreshIndicator
          pullDistance={pullDistance}
          isRefreshing={isRefreshing}
          maxPullDistance={maxPullDistance}
        />

        {/* Spaces Carousel — My Spaces tab only, show skeleton while loading */}
        {effectiveTab === 'my-spaces' && isAuthenticated && (hasSpaces || spacesLoading) && (
          <div className="w-full max-w-2xl mx-auto mt-2">
            <SpacesCarousel />
          </div>
        )}

        {/* Live pill row — isolated leaf between carousel and feed (spec §5.1). It owns its own
            layout + useNow tick, so it collapses to nothing when idle and re-renders only itself;
            the feed list below never ticks (spec §3). */}
        {showLiveSurfaces && <LiveHomePill />}

        {/* Meetverse live pill row — the same isolated-leaf slot idiom, on the Meetverse tab
            (recap/pill redesign §1). PUBLIC surface: it mounts for guests too (its endpoint and
            query carry no auth gate), so it must NOT ride showLiveSurfaces (my-spaces + authed);
            only the kill switch and the tab gate it. */}
        {featureFlags.liveEvents && effectiveTab === 'meetverse' && <LiveMeetversePills />}

        {/* Feed */}
        <div className="w-full max-w-2xl mx-auto px-4 py-2 mb-10">
          {isLoading || showMySpacesLoading ? (
            <>
              <FunnyLoadingMessage type="questions" className="mb-4" />
              <div className="flex flex-col gap-3.75">
                {QUESTION_FEED_SKELETON_KEYS.map(key => (
                  <QuestionCardSkeleton key={key} />
                ))}
              </div>
            </>
          ) : isError ? (
            <div className="flex items-center justify-center py-8">
              <p className="text-destructive text-base">Error loading posts. Please try again.</p>
            </div>
          ) : isEmpty || sortedQuestions.length === 0 ? (
            effectiveTab === 'my-spaces' ? (
              <MySpacesEmptyState hasSpaces={hasSpaces} />
            ) : (
              <HomeEmptyState sortBy={sortBy} onCreateQuestion={handleCreateQuestion} />
            )
          ) : (
            <HomeFeed
              questions={sortedQuestions}
              isLoadingMore={isLoadingMore}
              hasNextPage={hasNextPage}
              onLoadMore={fetchNextPage}
              // Recap section v2 rides INSIDE the feed as an injected list item
              // (recap/pill redesign §2) — still an isolated ticking leaf (§3).
              injectedItem={showLiveSurfaces ? <LiveHomeRecap /> : undefined}
            />
          )}
        </div>

        {/* Floating Action Button */}
        <HomeFAB onClick={handleCreateQuestion} />

        {/* Bottom Gradient */}
        <div className="fade-bottom-nav" />
      </div>
    </>
  );
};
